import React, {useCallback, useEffect, useRef, useState} from "react"
import {styled} from "@mui/material/styles"
import MuiDrawer, {drawerClasses} from "@mui/material/Drawer"
import {Toolbar, Tooltip} from "@mui/material"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"

// MUI "Mini variant drawer" pattern: permanent drawer that animates its width
// between an expanded state and a narrow collapsed state.
// https://mui.com/material-ui/react-drawer/#mini-variant-drawer
//
// The panel can be toggled by a thin full-height strip on its inner edge
// (right edge for a left drawer, left edge for a right one) — rendered only when
// `onToggle` is given. Without it the panel is controlled from outside (e.g. the
// burger button in the Header via MenuContext) and has no strip at all.
// When `onResize` is given, dragging the strip resizes the expanded panel.

export const HANDLE_WIDTH = 16
// icon column (48) + strip (16)
const DEFAULT_COLLAPSED_WIDTH = 64
// icon column only, no strip
const DEFAULT_COLLAPSED_WIDTH_NO_HANDLE = 56
const DRAG_THRESHOLD_PX = 4

const widthMixin = (theme, width, duration) => ({
    width,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration
    }),
    overflowX: "hidden"
})

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => !["open", "width", "collapsedWidth", "dragging"].includes(prop)
})(({theme, open, width, collapsedWidth, dragging}) => {
    const mixin = open
        ? widthMixin(theme, width, dragging ? 0 : theme.transitions.duration.enteringScreen)
        : widthMixin(theme, collapsedWidth, theme.transitions.duration.leavingScreen)
    return {
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...mixin,
        [`& .${drawerClasses.paper}`]: {
            boxSizing: "border-box",
            ...mixin
        }
    }
})

// Plain divs, not Box: App.css has global ".MuiBox-root a" / ".MuiBox-root" rules
// that would break the vertical menu and the stretch of the edge strip.
const Body = styled("div", {
    shouldForwardProp: (prop) => prop !== "anchor"
})(({anchor}) => ({
    display: "flex",
    flexDirection: anchor === "left" ? "row" : "row-reverse",
    alignItems: "stretch",
    flexGrow: 1,
    minHeight: 0
}))

const Content = styled("div", {
    shouldForwardProp: (prop) => prop !== "hideScrollbar"
})(({hideScrollbar}) => ({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    ...(hideScrollbar && {
        // still scrollable with wheel/touch, just no visible bar
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": {display: "none"}
    })
}))

const EdgeHandle = styled("div", {
    shouldForwardProp: (prop) => !["anchor", "resizable"].includes(prop)
})(({theme, anchor, resizable}) => ({
    flexShrink: 0,
    width: HANDLE_WIDTH,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: resizable ? "col-resize" : "pointer",
    color: theme.palette.text.disabled,
    userSelect: "none",
    [anchor === "left" ? "borderLeft" : "borderRight"]: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create(["background-color", "color"], {
        duration: theme.transitions.duration.shortest
    }),
    "&:hover, &:focus-visible": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.secondary,
        outline: "none"
    }
}))

/**
 * Collapsible side panel (left or right).
 *
 * @param {"left"|"right"} anchor
 * @param {boolean} open              expanded (true) or collapsed (false)
 * @param {function} [onToggle]       if given, an edge strip is rendered and clicking it calls this;
 *                                    omit to control the panel from outside (no strip)
 * @param {number} width              expanded width in px
 * @param {number} [collapsedWidth]   collapsed width in px (default 64 with strip, 56 without)
 * @param {function} [onResize]       if given, dragging the strip resizes the expanded panel;
 *                                    called once with the final width when the drag ends
 * @param {number} [minWidth]         resize lower bound
 * @param {number} [maxWidth]         resize upper bound
 * @param {string} label              accessible name of the panel
 * @param {boolean} [hideScrollbar]   scroll the content without a visible scrollbar
 *
 * Children are always rendered (so icon-only menus stay visible when collapsed);
 * hide wide content yourself when `open` is false.
 */
export default function CollapsibleDrawer({
    anchor = "left",
    open,
    onToggle,
    width = 230,
    collapsedWidth,
    onResize,
    minWidth = 180,
    maxWidth = 600,
    label = "panel",
    hideScrollbar = false,
    children,
    sx
}) {
    const isLeft = anchor === "left"
    const hasHandle = Boolean(onToggle)
    const resizable = hasHandle && Boolean(onResize) && open
    const effectiveCollapsedWidth = collapsedWidth ?? (hasHandle ? DEFAULT_COLLAPSED_WIDTH : DEFAULT_COLLAPSED_WIDTH_NO_HANDLE)
    const [dragWidth, setDragWidth] = useState(null)
    const drag = useRef(null)

    const clamp = useCallback(
        (w) => Math.min(maxWidth, Math.max(minWidth, Math.round(w))),
        [minWidth, maxWidth]
    )

    const onMouseDown = (event) => {
        if (event.button !== 0) return
        event.preventDefault()
        drag.current = {startX: event.clientX, startWidth: width, moved: false}
    }

    useEffect(() => {
        if (!hasHandle) return undefined
        const onMove = (event) => {
            const d = drag.current
            if (!d) return
            const dx = event.clientX - d.startX
            if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD_PX) return
            if (!resizable) return
            d.moved = true
            setDragWidth(clamp(d.startWidth + (isLeft ? dx : -dx)))
        }
        const onUp = () => {
            const d = drag.current
            if (!d) return
            drag.current = null
            if (d.moved) {
                setDragWidth((w) => {
                    if (w !== null) onResize(w)
                    return null
                })
            } else {
                onToggle()
            }
        }
        document.addEventListener("mousemove", onMove)
        document.addEventListener("mouseup", onUp)
        return () => {
            document.removeEventListener("mousemove", onMove)
            document.removeEventListener("mouseup", onUp)
        }
    }, [hasHandle, resizable, isLeft, clamp, onResize, onToggle])

    const onKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onToggle()
        }
    }

    const title = resizable
        ? `Click to collapse ${label}, drag to resize`
        : `${open ? "Collapse" : "Expand"} ${label}`
    const pointsLeft = isLeft ? open : !open

    return (
        <Drawer
            variant="permanent"
            anchor={anchor}
            open={open}
            width={dragWidth ?? width}
            collapsedWidth={effectiveCollapsedWidth}
            dragging={dragWidth !== null}
            sx={sx}
        >
            <Toolbar/>
            <Body anchor={anchor}>
                <Content hideScrollbar={hideScrollbar}>
                    {children}
                </Content>
                {hasHandle && <Tooltip title={title} placement={isLeft ? "right" : "left"} enterDelay={600}>
                    <EdgeHandle
                        anchor={anchor}
                        resizable={resizable}
                        role="button"
                        tabIndex={0}
                        aria-label={title}
                        aria-expanded={open}
                        onMouseDown={onMouseDown}
                        onKeyDown={onKeyDown}
                    >
                        {pointsLeft
                            ? <ChevronLeftIcon sx={{fontSize: HANDLE_WIDTH}}/>
                            : <ChevronRightIcon sx={{fontSize: HANDLE_WIDTH}}/>}
                    </EdgeHandle>
                </Tooltip>}
            </Body>
        </Drawer>
    )
}
